import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { API_ENDPOINTS } from '../utils/config';

export interface Company {
  _id: string;
  name: string;
  email: string;
  domain?: string;
  logo_url?: string;
  logo_gridfs_id?: string;
  logo_display_mode?: 'both' | 'logo_only' | 'name_only';
  logo_has_name?: boolean;
  website?: string;
  region?: string;
  feature_settings?: {
    sourcing_opportunities?: boolean;
    change_supplier?: boolean;
    document_match?: boolean;
  };
}

export interface User {
  _id: string;
  email: string;
  name: string;
  role: 'user' | 'admin' | 'procurement' | 'useradmin';
  is_admin?: boolean;
  department?: string;
  phone?: string;
  company_id?: string;
  company?: Company;
  permissions?: {
    open_rfq_request?: boolean;
    edit_supplier?: boolean;
    edit_product?: boolean;
    approve_ability?: boolean;
    see_all_requests?: boolean;
    user_rights?: boolean;
    edit_address?: boolean;
    edit_rfq_workflow?: boolean;
    edit_supplier_validity?: boolean;
    edit_reminder_settings?: boolean;
    control_ability?: boolean;
    rfq_operations?: boolean;
    add_rfq_supplier?: boolean;
    /** "RFQ İşleme": uyarılara karar (Kabul et / Reddet / Yoksay). */
    process_rfq?: boolean;
  };
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (
    email: string,
    password: string
  ) => Promise<{ success: boolean; error?: string }>;
  loginWithToken: (
    accessToken: string
  ) => Promise<{ success: boolean; error?: string; user?: User }>;
  logout: () => void;
  // Re-fetch /me and replace the cached user. Call this after any mutation
  // that changes user/company-level data (workflow settings, role
  // permissions, company.region, plan, etc.) so dependent pages don't keep
  // serving stale values.
  refreshUser: () => Promise<User | null>;
  isLoading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/** Same context without the provider check: shared widgets (e.g. the
 *  approved-supplier picker) also render in tests without an AuthProvider. */
export const useOptionalAuth = (): AuthContextType | undefined =>
  useContext(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for Portal token in URL (SSO redirect from Portal)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const portalToken = urlParams.get('token');

    if (portalToken) {
      console.log('🔐 Portal token detected in URL, logging in...');

      // Mark SSO login in progress to prevent race condition redirects
      sessionStorage.setItem('sso_login_in_progress', 'true');

      // Set token immediately to prevent redirect race condition
      setToken(portalToken);
      localStorage.setItem('auth_token', portalToken);

      // NOTE: Don't clean URL here — wait until /me completes
      // This prevents React 18 Strict Mode re-mount from losing the token

      // Validate and get user data
      fetch(API_ENDPOINTS.USERS + '/me', {
        headers: {
          Authorization: `Bearer ${portalToken}`,
          'Content-Type': 'application/json',
        },
      })
        .then(async (response) => {
          if (response.ok) {
            const data = await response.json();
            if (data.success && data.data) {
              console.log('✅ Portal SSO login successful');
              setUser(data.data);
              import('../utils/categories').then((m) =>
                m.loadCategoriesFromApi()
              );
              localStorage.setItem('auth_user', JSON.stringify(data.data));
            } else {
              console.error(
                '❌ Portal token validation failed - invalid response'
              );
              setToken(null);
              localStorage.removeItem('auth_token');
            }
          } else {
            console.error('❌ Portal token validation failed - HTTP error');
            setToken(null);
            localStorage.removeItem('auth_token');
          }
          // Clean URL AFTER processing is complete
          window.history.replaceState({}, '', window.location.pathname);
          sessionStorage.removeItem('sso_login_in_progress');
          setIsLoading(false);
        })
        .catch((error) => {
          console.error('Portal token validation error:', error);
          setToken(null);
          localStorage.removeItem('auth_token');
          window.history.replaceState({}, '', window.location.pathname);
          sessionStorage.removeItem('sso_login_in_progress');
          setIsLoading(false);
        });
      return; // Don't check saved token if we have a URL token
    }

    // Check for saved token if no URL token
    const savedToken = localStorage.getItem('auth_token');
    const savedUser = localStorage.getItem('auth_user');

    if (savedToken && savedUser) {
      try {
        // Validate token AND get fresh user data from /me
        fetch(API_ENDPOINTS.USERS + '/me', {
          headers: {
            Authorization: `Bearer ${savedToken}`,
            'Content-Type': 'application/json',
          },
        })
          .then(async (response) => {
            if (response.ok) {
              const data = await response.json();
              if (data.success && data.data) {
                // Use FRESH user data from /me (not stale localStorage)
                setToken(savedToken);
                setUser(data.data);
                import('../utils/categories').then((m) =>
                  m.loadCategoriesFromApi()
                );
                localStorage.setItem('auth_user', JSON.stringify(data.data));
              } else {
                // Response ok but data invalid, use cached as fallback
                setToken(savedToken);
                setUser(JSON.parse(savedUser));
              }
            } else {
              // Token is invalid, clear storage
              console.log('Token expired or invalid, clearing storage');
              localStorage.removeItem('auth_token');
              localStorage.removeItem('auth_user');
              localStorage.removeItem('user_type');
            }
            setIsLoading(false);
          })
          .catch((error) => {
            console.error('Error validating token:', error);
            localStorage.removeItem('auth_token');
            localStorage.removeItem('auth_user');
            localStorage.removeItem('user_type');
            setIsLoading(false);
          });
      } catch (error) {
        console.error('Error parsing saved user data:', error);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        localStorage.removeItem('user_type');
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  }, []);

  const fetchCurrentUser = async (
    accessToken: string
  ): Promise<{ success: boolean; user?: User; error?: string }> => {
    try {
      console.log('🔍 fetchCurrentUser: Calling /me endpoint...');
      const meResponse = await fetch(API_ENDPOINTS.USERS + '/me', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      console.log(
        '🔍 fetchCurrentUser: Response status:',
        meResponse.status,
        meResponse.statusText
      );

      if (!meResponse.ok) {
        const errorPayload = await meResponse.json().catch(() => ({}));
        const message =
          (errorPayload && (errorPayload.error || errorPayload.message)) ||
          'Failed to get user data';
        console.error('❌ fetchCurrentUser: Response not OK:', message);
        return { success: false, error: message };
      }

      const meData = await meResponse.json();
      console.log('🔍 fetchCurrentUser: Response data:', {
        success: meData.success,
        hasData: !!meData.data,
        error: meData.error,
      });

      if (meData.success && meData.data) {
        return { success: true, user: meData.data };
      }

      console.error('❌ fetchCurrentUser: Invalid response structure:', meData);
      return {
        success: false,
        error: meData.error || 'Failed to get user data',
      };
    } catch (error) {
      console.error('❌ fetchCurrentUser: Exception:', error);
      return { success: false, error: 'Failed to get user data' };
    }
  };

  const applyAuthenticatedSession = (accessToken: string, userData: User) => {
    setToken(accessToken);
    setUser(userData);
    localStorage.setItem('auth_token', accessToken);
    localStorage.setItem('auth_user', JSON.stringify(userData));
  };

  const login = async (
    email: string,
    password: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetch(API_ENDPOINTS.USERS + '/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Login failed');
      }

      const data = await response.json();

      if (data.success) {
        const { access_token } = data.data;

        if (!access_token) {
          console.error('Missing access_token in response');
          return { success: false, error: 'Missing access token' };
        }

        const currentUserResult = await fetchCurrentUser(access_token);
        if (currentUserResult.success && currentUserResult.user) {
          applyAuthenticatedSession(access_token, currentUserResult.user);
          return { success: true };
        }

        console.error('Failed to get user data from /me endpoint');
        return {
          success: false,
          error: currentUserResult.error || 'Failed to get user data',
        };
      } else {
        throw new Error(data.error || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Login failed',
      };
    }
  };

  const loginWithToken = async (
    accessToken: string
  ): Promise<{ success: boolean; error?: string; user?: User }> => {
    console.log(
      '🔍 loginWithToken called with token length:',
      accessToken?.length
    );
    try {
      const currentUserResult = await fetchCurrentUser(accessToken);
      console.log('🔍 fetchCurrentUser result:', {
        success: currentUserResult.success,
        hasUser: !!currentUserResult.user,
        error: currentUserResult.error,
      });

      if (currentUserResult.success && currentUserResult.user) {
        applyAuthenticatedSession(accessToken, currentUserResult.user);
        console.log('✅ SSO login successful, user authenticated');
        return { success: true, user: currentUserResult.user };
      }

      console.error('❌ SSO login failed:', currentUserResult.error);
      return {
        success: false,
        error: currentUserResult.error || 'Failed to complete SSO login',
      };
    } catch (error) {
      console.error('❌ loginWithToken exception:', error);
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Failed to complete SSO login',
      };
    }
  };

  const refreshUser = async (): Promise<User | null> => {
    // Use the current token from React state. Fall back to localStorage in
    // case refreshUser is called during a transition where setToken hasn't
    // committed yet (e.g. immediately after login).
    const activeToken = token || localStorage.getItem('auth_token');
    if (!activeToken) return null;

    const result = await fetchCurrentUser(activeToken);
    if (result.success && result.user) {
      setUser(result.user);
      localStorage.setItem('auth_user', JSON.stringify(result.user));
      return result.user;
    }
    return null;
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    localStorage.removeItem('user_type');
    // Belt-and-braces cross-app logout signal: portal's bootstrap reads
    // this cookie and wipes its own auth even if it never sees the
    // /logout route (e.g. an older cached bundle). For the cookie to be
    // visible across subdomains in prod (rfq2.gordiananalytics.com →
    // rfq2-portal.gordiananalytics.com) we must explicitly set the apex
    // Domain attribute. Without it the cookie is host-scoped and never
    // reaches the portal — which is exactly the prod bug we hit. On
    // localhost we skip the Domain attribute since the two ports already
    // share the host.
    try {
      const host = window.location.hostname;
      const isLocal = host === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(host);
      const apex = isLocal ? '' : host.split('.').slice(-2).join('.');
      const domainAttr = apex ? `; domain=.${apex}` : '';
      document.cookie = `gordian_force_logout=1; path=/; max-age=60${domainAttr}`;
    } catch {
      /* ignore */
    }
    // Bounce through the portal's /logout so the portal session is killed
    // too. Without this, signing out of RFQ just leaves the user signed
    // back into the portal, which feels like nothing happened.
    const portalUrl =
      process.env.REACT_APP_PORTAL_URL || 'http://localhost:3001';
    window.location.href = `${portalUrl.replace(/\/$/, '')}/logout`;
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    loginWithToken,
    logout,
    refreshUser,
    isLoading,
    isAuthenticated: !!user && !!token,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
